import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import confetti from "canvas-confetti";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const celebrate = () => {
  const colors = ["#D4AF37", "#E8A888", "#284B63", "#FAF8F5", "#B76E79"];
  confetti({ particleCount: 90, spread: 75, origin: { y: 0.6 }, colors, scalar: 0.9 });
  setTimeout(() => confetti({ particleCount: 50, angle: 60, spread: 60, origin: { x: 0, y: 0.65 }, colors, scalar: 0.8 }), 350);
  setTimeout(() => confetti({ particleCount: 50, angle: 120, spread: 60, origin: { x: 1, y: 0.65 }, colors, scalar: 0.8 }), 550);
  setTimeout(() => confetti({ particleCount: 35, spread: 100, origin: { y: 0.4 }, colors, shapes: ["star"], scalar: 1.1 }), 900);
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const { clearCart } = useCart();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const maxAttempts = 8;
    const tick = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          clearCart();
          celebrate();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts >= maxAttempts) { setStatus("error"); return; }
        setTimeout(tick, 2000);
      } catch {
        if (attempts >= maxAttempts) setStatus("error");
        else setTimeout(tick, 2000);
      }
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-32 text-center relative" data-testid="payment-success-page">
      <div className="absolute left-1/2 top-20 -translate-x-1/2 w-96 h-96 bg-peach/40 organic-blob blur-3xl -z-0" />
      <div className="relative">
        {status === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="mx-auto mb-6 text-lapis animate-spin" size={40} strokeWidth={1.2} />
            <h1 className="font-serif text-4xl text-ink font-light italic">a soft pause…</h1>
            <p className="mt-4 text-ink/60">confirming your purchase</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="payment-paid">
            <CheckCircle2 className="mx-auto mb-6 text-sage-deep" size={56} strokeWidth={1.2} />
            <h1 className="font-serif text-4xl md:text-5xl text-ink font-light">
              Thank you,<br /><em className="text-peach-deep italic">truly</em>.
            </h1>
            <p className="mt-6 text-ink/65 leading-relaxed max-w-md mx-auto">
              Your offering has been received. Details and access will arrive in your inbox shortly.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/dashboard" className="btn-primary" data-testid="payment-go-dashboard">Visit your space</Link>
              <Link to="/shop" className="btn-soft" data-testid="payment-back-shop">Back to shop</Link>
            </div>
          </div>
        )}
        {(status === "expired" || status === "error") && (
          <div data-testid="payment-error">
            <XCircle className="mx-auto mb-6 text-peach-deep" size={56} strokeWidth={1.2} />
            <h1 className="font-serif text-4xl text-ink font-light">Something paused that.</h1>
            <p className="mt-6 text-ink/65">No charge was made. You can try again whenever it feels right.</p>
            <div className="mt-10"><Link to="/shop" className="btn-primary">Back to shop</Link></div>
          </div>
        )}
      </div>
    </div>
  );
}
